import {
  Card,
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import dayjs from 'dayjs';
import type { Destination } from '@/features/destinations/utils/Destination';
import useAppointmentStore, {
  isInBranchAppointment,
} from '@/store/AppointmentStore';
import useThemeStore from '@/store/ThemeStore';

interface AgentInfoConfirmationCardProps {
  agentName: string;
  selectedDate?: Date;
  selectedStartTime?: string;
  selectedEndTime?: string;
  selectedDestination?: Destination;
}

function AgentInfoContactInfoPage({
  agentName,
  selectedDate,
  selectedStartTime,
  selectedEndTime,
  selectedDestination,
}: AgentInfoConfirmationCardProps) {
  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );
  const { isLuxuryTheme } = useThemeStore();
  const isInBranchFlow = useAppointmentStore(isInBranchAppointment);

  return (
    <Stack
      direction={'row'}
      flexWrap={'wrap'}
      data-testid='callInfoCard'
      sx={{
        width: '100%',
        textAlign: 'left',
      }}
      justifyContent={'center'}
      alignItems={'center'}
    >
      <Card
        sx={[
          {
            marginBottom: '16px',
            padding: '1.5rem 1rem 1rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            gap: '20px',
          },
          isLuxuryTheme && {
            backgroundColor: 'background.default',
            border: '1px solid',
            borderColor: 'primary.main',
          },
        ]}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
          <Icon
            iconName='calendar-check-duotone'
            color='primary'
            sx={{
              width: {
                xs: 24,
                lg: 48,
              },
              height: {
                xs: 24,
                lg: 48,
              },
            }}
          />
          <Typography
            sx={[
              {
                fontWeight: '700',
                fontSize: { xs: '16px', lg: '24px' },
              },
              isLuxuryTheme && {
                fontSize: {
                  xs: '27px',
                },
                fontFamily: 'Domine',
              },
            ]}
          >
            30-minute {isInBranchFlow ? 'visit' : 'call'} with{' '}
            {agentName.split(' ')[0]}
          </Typography>
        </Stack>
        <Stack
          direction='column'
          spacing='10px'
          sx={{
            '& .MuiTypography-root': {
              textAlign: 'left',
              fontSize: {
                xs: '16px',
                lg: '20px',
              },
              display: 'inline',
              '& span': {
                fontWeight: '400',
              },
            },
          }}
        >
          <Typography variant='h6Medium' data-testid='dateInfo'>
            {'Date: '}
            <span>{dayjs(selectedDate).format('dddd, MMMM D, YYYY')}</span>
          </Typography>
          <Typography variant='h6Medium' data-testid='timeInfo'>
            {'Time: '}
            <span>
              {`${dayjs(selectedStartTime).format('hh:mm a')} - ${dayjs(selectedEndTime).format('hh:mm a')}`}
            </span>
          </Typography>
          <Typography variant='h6Medium' data-testid='destinationInfo'>
            {'Destination: '}
            <span>{searchedDestination || selectedDestination?.name}</span>
          </Typography>
        </Stack>
      </Card>
    </Stack>
  );
}

export default AgentInfoContactInfoPage;
